// Where a part goes by what it is dropped on, and what lies on a room's top
// (TODO.md, *Electrical schematic on a sheet*, decisions 22 / 23): a junction
// box on a room's ceiling, upright against a wall, else on the floor; anything
// lying on a room's top follows the room's height. Pure and DOM-free
// (test/mounting.test.mjs; docs/claude/products.md, *Mounted by where it is
// dropped*).

import type {
  PartMount,
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  rotatePoint,
  toInstanceLocal,
} from './geometry';
import {
  partHeight,
  roomBase,
} from './heights';
import {
  ceilingTop,
  ceilingUnder,
} from './lights';
import type { LocalBox } from './roomSnap';
import {
  isTilted,
  tiltedExtents,
} from './tilt';

/** A wall-mounted part's Rotation X: its top face turned onto the wall, which
 *  is its local −y side. */
export const WALL_MOUNT_TILT = 90;
/** How near a room's top a part's underside lies to lie on it (mm). */
export const ON_TOP_TOL = 1;

/** A room's top over a world point (mm above the ground): its ceiling's top
 *  there — a shaped one's too; beyond its walls, its edge's nearest —, lifted
 *  by the room's own Height from ground. */
export function roomTopAt(room: ProductInstance, p: Vec2): number {
  const local = toInstanceLocal(room, p);
  return roomBase(room) + ceilingTop(room)(local);
}

/** Whether a part lies on a room's top: its own Height from ground is the
 *  top's over its middle. A room never does, nor a part on its default base. */
export function restsOnTop(room: ProductInstance, part: ProductInstance): boolean {
  if (part.autoBenches || part === room || part.heightFromGround == null) return false;
  return Math.abs(part.heightFromGround - roomTopAt(room, { x: part.x, y: part.y })) <= ON_TOP_TOL;
}

/** Lay a part on a room's top, over its middle (whole mm). */
export function layOnTop(room: ProductInstance, part: ProductInstance): void {
  part.heightFromGround = Math.round(roomTopAt(room, { x: part.x, y: part.y }));
}

/** The Rotation X of a part stood on a wall: its top face on the wall
 *  (`WALL_MOUNT_TILT`), or its underside (`wallMountUnderside`: its top facing
 *  out, its front pointing down). */
export function wallMountTilt(m: Pick<PartMount, 'wallMountUnderside'>): number {
  return m.wallMountUnderside ? -WALL_MOUNT_TILT : WALL_MOUNT_TILT;
}

/** Whether a mount seats a copy anywhere (else the drop leaves it be). */
export function mountsByDrop(m: PartMount): boolean {
  return !!(m.onRoomTop || m.underCeiling || m.wallMountCentre != null || m.wallMountBelowCeiling != null || m.inWall != null || m.backOnWall);
}

type Placing = Pick<ProductInstance, 'x' | 'y' | 'rotation'>;

/** The rest of a product's parts carried with the one seated on a wall
 *  (`backOnWall`), as one: turned about where it stood by as much as it turned,
 *  and moved with it — so they keep where they stand from it. */
export function carryWith(before: Placing, after: Placing, others: Placing[]): void {
  const turn = after.rotation - before.rotation;
  for (const o of others) {
    const p = rotatePoint({ x: o.x - before.x, y: o.y - before.y }, turn);
    o.x = after.x + p.x;
    o.y = after.y + p.y;
    o.rotation = (((o.rotation + turn) % 360) + 360) % 360;
  }
}

/** The underside of a room's ceiling over a world point (mm above the ground):
 *  a flat ceiling's level, a shaped one's surface there, lifted by the room's
 *  own Height from ground. */
export function ceilingUnderAt(room: ProductInstance, p: Vec2): number {
  const local = toInstanceLocal(room, p);
  return roomBase(room) + ceilingUnder(room, local).z;
}

/** Whether a part hangs under a room's ceiling: upright, its top the ceiling's
 *  underside over its middle. */
export function hangsUnder(room: ProductInstance, part: ProductInstance): boolean {
  if (part.autoBenches || part === room || part.heightFromGround == null || isTilted(part)) return false;
  return Math.abs(part.heightFromGround + partHeight(part) - ceilingUnderAt(room, { x: part.x, y: part.y })) <= ON_TOP_TOL;
}

/** Hang a part under a room's ceiling, over its middle (whole mm). */
export function hangUnder(room: ProductInstance, part: ProductInstance): void {
  part.heightFromGround = Math.round(ceilingUnderAt(room, { x: part.x, y: part.y }) - partHeight(part));
}

/** The middle (mm above the ground) of a part standing on a wall (tilted). */
function uprightMiddle(part: ProductInstance): number {
  return (part.heightFromGround ?? 0) + uprightExtents(part).hz / 2;
}

/** Whether a part stands on a room's wall with its middle `below` mm under the
 *  room's ceiling there. */
export function standsBelowCeiling(room: ProductInstance, part: ProductInstance, below: number): boolean {
  if (part.autoBenches || part === room || part.heightFromGround == null || !isTilted(part)) return false;
  return Math.abs(uprightMiddle(part) - (ceilingUnderAt(room, { x: part.x, y: part.y }) - below)) <= ON_TOP_TOL;
}

/** Stand a part's middle `below` mm under a room's ceiling over it (whole mm). */
export function seatBelowCeiling(room: ProductInstance, part: ProductInstance, below: number): void {
  part.heightFromGround = wallMountBase(part, ceilingUnderAt(room, { x: part.x, y: part.y }) - below);
}

/** A part set into a wall (`inWall`): its front band, in its own frame — what
 *  stands out of the wall's face, the rest of it in the wall. Null for any
 *  other part. */
export function inWallBand(part: Pick<ProductInstance, 'inWall' | 'width' | 'depth'>): LocalBox | null {
  const d = part.inWall;
  if (d == null || !(d >= 0)) return null;
  return { x0: -part.width / 2, y0: part.depth / 2 - d, x1: part.width / 2, y1: part.depth / 2 };
}

/** A part's size as it stands upright on a wall (`WALL_MOUNT_TILT`): its plan
 *  half-sizes (along the wall, off it) and its height. */
function uprightExtents(part: ProductInstance): { hw: number; hd: number; hz: number } {
  return tiltedExtents({ ...part, rotationX: WALL_MOUNT_TILT, rotationY: 0 }, { w: part.width, d: part.depth, h: partHeight(part) });
}

/** The plan box, in its own frame, of a part stood upright on a wall: what sits
 *  flush on the wall's face. */
export function wallMountBox(part: ProductInstance): LocalBox {
  const e = uprightExtents(part);
  return { x0: -e.hw, y0: -e.hd, x1: e.hw, y1: e.hd };
}

/** The Height from ground (its lowest point, whole mm) of a part standing
 *  upright on a wall with its middle `centre` mm above the ground. */
export function wallMountBase(part: ProductInstance, centre: number): number {
  return Math.round(centre - uprightExtents(part).hz / 2);
}
